import BrandLogo from "@/components/BrandLogo";
import useBranding from "@/hooks/useBranding";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, UserCircle, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useFeatures } from "@/contexts/FeatureContext";

const navBase = [
  { to: "/", label: "Home", key: null },
  { to: "/restaurant", label: "Restaurant", key: "restaurant_ordering" },
  { to: "/grocery", label: "Mart", key: "grocery_ordering" },
  { to: "/daily-menu", label: "Daily Menu", key: "daily_menu" },
  { to: "/promotions", label: "Promotions", key: "promotions" },
  { to: "/catering", label: "Catering", key: "catering" },
  { to: "/lechon", label: "Lechon", key: "lechon_reservation" },
  { to: "/rewards", label: "Rewards", key: "royal_rewards" },
  { to: "/contact", label: "Contact", key: null },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { isOn } = useFeatures();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { branding } = useBranding();

  const navItems = navBase.filter((n) => !n.key || isOn(n.key));

  return (
    <header className="sticky top-0 z-50 bg-[#FAF9F6]/95 backdrop-blur-md border-b border-[#8A9A86]/20">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 lg:px-8 py-3">
        <Link to="/" data-testid="nav-logo">
          <BrandLogo
            src={branding.header_logo}
            label="Maharlika"
            sublabel="Seafood Mart & Chow"
            variant="light"
            size="lg"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#2C4A3B]" : "text-[#1A2421]/70 hover:text-[#2C4A3B]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/cart"
            data-testid="nav-cart-button"
            className="relative w-10 h-10 rounded-full bg-[#2C4A3B] text-[#FAF9F6] flex items-center justify-center hover:bg-[#1E3329] transition-colors"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-[#050505] text-[10px] font-bold rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to={user.role === "customer" ? "/account" : "/admin"}
                data-testid="nav-profile"
                className="px-3 py-2 text-sm font-medium text-[#1A2421] hover:text-[#2C4A3B] flex items-center gap-1.5"
              >
                <UserCircle className="w-5 h-5" strokeWidth={1.5} />
                {user.name?.split(" ")[0]}
              </Link>
              <button
                data-testid="nav-logout"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="text-xs text-[#5C6B64] hover:text-[#8B0000]"
              >
                Logout
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden md:inline-flex px-4 py-2 text-sm font-medium border border-[#2C4A3B] text-[#2C4A3B] rounded-full hover:bg-[#2C4A3B] hover:text-[#FAF9F6] transition-colors"
            >
              Sign in
            </Link>
          )}

          <button
            data-testid="nav-mobile-menu"
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A2421]"
            onClick={() => setOpen(!open)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#8A9A86]/20 bg-[#FAF9F6]">
          <div className="px-4 py-3 grid grid-cols-2 gap-1">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-sm font-medium text-[#1A2421]/80 hover:bg-[#F2EFEB] rounded"
              >
                {n.label}
              </Link>
            ))}

            {!user && (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-login"
                className="col-span-2 text-center px-3 py-2 mt-2 text-sm font-medium border border-[#2C4A3B] text-[#2C4A3B] rounded-full"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
